/**
 * HOW TO PLAY: one modal, categories down the side, and a page per mini-game.
 *
 * The player's words: "we MUST have some instructions so users can see each of the games
 * explained outside of game play." A duel is ten seconds long and both players are frozen for
 * all of it, which is the worst possible moment to be learning what a hidden arm point is.
 *
 * **THIS IS THE EXPANSION OF THE ONE THAT WAS ALREADY THERE, NOT A SECOND ONE BESIDE IT.**
 * `routes/index.tsx` had a `Dialog` around a flat `HowToPlay` component; that content is the
 * Gameplay page below, the power-ups half of it is its own page, and the mini-games are pages
 * this file does not know the names of.
 *
 * ## A `Dialog` IS CORRECT HERE, AND THAT IS NOT AN EXEMPTION FROM THE ARENA RULE
 *
 * Anything drawn over a **live** arena is an `ArenaPanel` — `role="region"`, no backdrop —
 * because a modal's backdrop swallows every pointer sample before it reaches the canvas and
 * `role="dialog"` is what `keyboardActs` reads to disarm SPACE / W / Q. That is why the room
 * roster and the sayings picker had to stop being modals.
 *
 * Neither half of that cost applies to either of this modal's two entry points:
 *
 * - **The lobby**, where there is no arena at all.
 * - **The death screen**, where the player has just been eaten. There is no blob to steer, no
 *   split to press and no chase to escape — which is exactly the reasoning that keeps Overlay
 *   Settings a `Dialog` while the roster and the picker are not, and the same reasoning that
 *   hands a dead player the app header back (CLAUDE.md, "The app bar gets out of the way of a
 *   live match"). A deliberate pause is the entire feature; nothing is happening behind it that
 *   this player is part of.
 *
 * **IT MUST NOT BE REACHABLE FROM A LIVE ARENA**, and that is a property rather than a promise:
 * the only file under `game/` that mounts it is `hud/DeathScreen.tsx`, which `GameCanvas` draws
 * only once `hud.death` is set and the swallow has played. `instructions.test.tsx` reads the
 * arena's own sources off disk and fails if a second one appears.
 *
 * ## THE MINI-GAME PAGES ARE GENERATED, AND THE EXAMPLES ARE THE REAL COMPONENTS
 *
 * The page list is `instructionPages` over `MINI_GAMES` — see that module for why a hand-written
 * list is the thing being designed out. What each page then draws is the *same components the
 * duel draws*, never a screenshot and never a second drawing:
 *
 * - the choices, through `MiniGameChoiceIcon` over the registry's own `MiniGameChoiceArt`;
 * - the pot, through `DuelPotBar` over `readDuelPot`, driven by a static percentage instead of
 *   the wire — which is what that component was extracted for.
 *
 * The player asked for "the actual graphic that would be shown in game so they can see the
 * example". A screenshot is wrong the first time a rung moves; a live component cannot drift.
 *
 * ## THE STAR PAGE, AND WHICH OF ITS TWO POSSIBLE EXAMPLES IS THE HONEST ONE
 *
 * The star is a third fixed page — see `./instructions.ts` for why it cannot arrive through
 * `MINI_GAMES` — and there were two candidates for the picture on it:
 *
 * - **The star itself, as `game/scenes/star.ts` draws it.** Refused, and not on taste: that
 *   module is the *arithmetic* of a shape a Phaser `Graphics` fills — ten corners from
 *   `starCorner`, ten wedges of `hueColor`, a halo and a core, drawn inside `ArenaScene`. There
 *   is no component to mount, so putting the star on this page would mean writing a **second
 *   renderer** for it in SVG. A second drawing is exactly what this modal exists not to have: the
 *   card ahead of this one turned the star rainbow, and a hand-rolled copy is wrong the first
 *   time that happens again. (`hud/minimap.ts` does expose an SVG `starPath`, and it is
 *   deliberately not reused here either — that is the *blip*, a different picture of the same
 *   thing, and mounting it would teach the panel rather than the field.)
 * - **`StarChoiceCard`, which is what is mounted.** It is a real React component that takes a
 *   `VoteView` and a mass and nothing else — no connection, no wire — which is the same property
 *   that let `DuelPotBar` be mounted here, and it is the card the whole mechanic turns on: the
 *   auto-win against the gamble, the actual payout figure, and the word DEATH. A player who has
 *   never seen a star has ten seconds to make that choice while being chased, and this is the one
 *   surface where they can read it with the clock stopped.
 *
 * **THE EXAMPLE IS LIVE RATHER THAN DISABLED, AND THAT IS DELIBERATE.** The alternative was
 * `canAnswer: false`, which draws every control greyed out — the *spectator's* reading, and a lie
 * about a control the reader will very much be able to press. So the page holds the answer the
 * way the connection holds it, the card behaves exactly as it does in a room (a press reads SENT,
 * the buttons then go inert because the first answer wins, "Take the share" closes the card
 * because the auto-win *is* the absence of an answer), and nothing on it does something it would
 * not do in game. What that costs is one control this page owns: **Reset**, which re-mounts the
 * card — because a reader who tries the safe option would otherwise be left looking at the space
 * where the example was.
 *
 * The clock is **frozen** at the full `VOTE_DURATION_SECONDS`, which is `DuelPotBar`'s `settled`
 * one card along: this is a still picture, there is no room and nothing is counting down. What
 * that leaves running is `VoteSeconds`' own one-second timer setting the same number forever,
 * which React drops without a render.
 *
 * ## THE SMALL SCREEN
 *
 * The tighter of the two entry points is the death panel on a 390-pixel landscape phone, which
 * is already dropping its two reserved slots for height. The two axes are answered separately,
 * because they are two different problems:
 *
 * - **Height** is the card's, capped at `85dvh` with the card itself scrolling —
 *   `HudSettingsDialog`'s arrangement, and the one that carries the 390-pixel landscape case.
 * - **Width** decides the nav's *shape*: a wrapping row of chips above the page below `sm`, a
 *   column beside it above it. So a phone held sideways — 844 wide — gets the column, and a
 *   phone held upright gets the chips. It wraps rather than scrolling sideways, because a nav
 *   that hides its own last entry off the right edge is a nav nobody finds the third mini-game
 *   in.
 *
 * The page body carries `min-w-0`, which is the one thing that stops a flex child refusing to
 * shrink below its content and pushing the whole card sideways. A page that scrolls down is
 * fine; one that scrolls across is not.
 *
 * `sm:` rather than `useCompactHud`: this modal is not in the HUD's zone table and is drawn on a
 * route with no arena at all, so what it needs is the nav's shape against the width — which is
 * exactly what a width breakpoint is — and not the arena's both-axes question about whether
 * there is room to play.
 */

import {
  MAX_STARS_PER_ROOM,
  POWER_UP_INVENTORY_SIZE,
  STAR_GAMBLE_PAYOUT_MULTIPLIER,
  VOTE_DURATION_SECONDS,
  VOTE_LOSS_SHARE,
} from '@chompdome/shared/constants';
import type { MiniGame } from '@chompdome/shared/minigames';
import {
  MINI_GAME_POT_MAX_PERCENT,
  MINI_GAMES,
  miniGameHowToPlay,
} from '@chompdome/shared/minigames';
import { POWER_UPS } from '@chompdome/shared/powerups';
import { type ReactNode, useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { readDuelPot } from '@/game/hud/duel-pot';
import { DuelCard } from '@/game/hud/DuelOverlay';
import { StarChoiceCard } from '@/game/hud/StarChoiceCard';
import { STAR_FIELD_LABEL } from '@/game/scenes/field-labels';
import {
  INSTRUCTION_STAR_SAMPLE_MASS,
  type InstructionPage,
  instructionPages,
  DUEL_EXAMPLE_STEP_MS,
  DUEL_EXAMPLE_WINDOW_MS,
  instructionDuel,
  instructionStarVote,
} from './instructions';

export interface InstructionsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The registry to build pages from. Injected for a test, exactly as `DuelOverlay` takes `now`:
   * what is worth checking is that a page appears for whatever is registered, not that the two
   * games registered today have one. Production never passes it.
   */
  games?: readonly MiniGame[];
}

export function InstructionsDialog({ open, onClose, games = MINI_GAMES }: InstructionsDialogProps) {
  const pages = instructionPages(games);
  const [pageId, setPageId] = useState(pages[0].id);
  // A page removed under the reader — a registry that shrank between renders, which only a test
  // does — falls back to the first rather than rendering nothing.
  const page = pages.find((candidate) => candidate.id === pageId) ?? pages[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="How to play"
      description="One arena, up to 100 players, last blob standing."
      /*
       * Wider than the app's default `max-w-md`, because this card is a nav beside a page rather
       * than a column of prose.
       *
       * **THE CARD IS A FIXED BOX AND THE PAGE SCROLLS INSIDE IT, WHICH REVERSES HOW THIS SCROLLED
       * BEFORE.** It used to be `max-h-[85dvh] overflow-y-auto` on the dialog itself, so the whole
       * card grew with whatever page was open and the *nav scrolled away with the page* — the
       * categories left the screen exactly when a reader wanted to pick another one. It also meant
       * the modal's height changed every time you switched page, which on a laptop read as the
       * dialog jumping about.
       *
       * **THE DEFINITE HEIGHT IS ON THE ROW BELOW RATHER THAN ON THE DIALOG, AND THAT IS THE ONLY
       * ARRANGEMENT THAT ACTUALLY SCROLLS.** `Dialog`'s panel is a plain block — a title, a
       * description and `<div className="mt-4">{children}</div>` — so it is *not* a flex column and
       * `h-full` on a child has no definite height to resolve against: the row would grow to its
       * content, the panel would clip it, and the page body would never become a scroll container
       * at all, because it fits its own content perfectly well. That is a page whose bottom is
       * simply unreachable. Sizing the row instead makes its height definite, which is what lets
       * `min-h-0` + `overflow-y-auto` on the page body do their job, and the panel then sizes
       * itself to the row exactly as it always has. The shared `Dialog` is untouched, which it
       * has to be — every other modal in the app is a block and wants to stay one.
       */
      className="max-w-4xl overflow-hidden"
    >
      {/*
       * `h-full` + `min-h-0` down this whole chain is what actually hands the overflow to the page
       * body: a flex child defaults to `min-height: auto` and so refuses to shrink below its
       * content, which would push the scroll back out to the dialog and undo the paragraph above.
       */}
      <div
        data-testid="instructions"
        /*
         * A fixed height, capped for the shape with the least of it there is — a phone held
         * sideways, which is what the death panel is drawn on (`HudSettingsDialog`'s reason). The
         * cap is what stops the fixed height being taller than the screen it is drawn on.
         */
        className="flex h-[32rem] max-h-[68dvh] min-h-0 flex-col gap-3 sm:flex-row sm:gap-5"
      >
        <PageNav pages={pages} current={page.id} onPick={setPageId} />
        {/*
         * `min-w-0` is load-bearing rather than tidy: a flex child defaults to `min-width: auto`
         * and therefore refuses to shrink below its content, so one long word or one wide row
         * inside a page would push the card sideways on a phone. Vertical scrolling is the
         * card's; horizontal scrolling is a bug.
         */}
        <div
          data-testid="instructions-page"
          data-page={page.id}
          className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1"
        >
          <PageBody page={page} />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * The categories: a column on a roomy screen, a wrapping row of chips on a narrow one.
 *
 * A row rather than a `<select>` on the small surface, because there are a handful of pages and
 * a chip says what the others are without being opened. It wraps rather than scrolling
 * sideways — a nav that hides its own last entry off the right edge is a nav a player never
 * finds the third mini-game in.
 *
 * `aria-current="page"` rather than a pressed state: these select which page is shown, which is
 * what that attribute means, and it is what the tests read the selection off.
 */
function PageNav({
  pages,
  current,
  onPick,
}: {
  pages: readonly InstructionPage[];
  current: string;
  onPick: (id: string) => void;
}) {
  return (
    <nav
      aria-label="Instruction categories"
      className="flex shrink-0 flex-wrap gap-1.5 sm:w-36 sm:flex-col sm:flex-nowrap"
    >
      {pages.map((page) => {
        const active = page.id === current;
        return (
          <button
            key={page.id}
            type="button"
            onClick={() => onPick(page.id)}
            aria-current={active ? 'page' : undefined}
            className={`cursor-pointer rounded-lg px-2.5 py-1.5 text-left text-xs font-semibold transition-colors sm:text-sm ${
              active
                ? 'bg-primary/15 text-primary'
                : 'bg-secondary/60 text-muted-foreground hover:text-foreground'
            }`}
          >
            {page.label}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * The shape of every page's body, in one place because every page has to be the same shape.
 *
 * **TWO COLUMNS ON A ROOMY SCREEN, ONE EVERYWHERE ELSE.** These pages are mostly short blocks —
 * a paragraph, a list of rules, a worked example — and a single column of them on a desktop is a
 * tall ribbon of text down the middle of a wide card, which is what made this modal fill a laptop
 * screen top to bottom. Two columns roughly halves that, which is the whole of the fix.
 *
 * **Two rather than three, and it is arithmetic rather than taste.** The card is `max-w-4xl`
 * (56rem) and the nav takes 9rem of it, so a page has ~44rem to spend: two columns are ~21rem
 * each, and three would be ~13rem — narrower than the pot bar's own ruler needs before
 * `potScaleTicks` starts dropping labels to fit. A column that makes the worked examples
 * illegible is not a column worth having.
 *
 * **The break rules are per element rather than blanket `break-inside-avoid`.** A list is exactly
 * the thing that *should* be allowed to flow from one column to the next — it is most of the
 * height on these pages, and forbidding it to break would leave one enormous column and one empty
 * one. What may not break is a single bullet (a rule split down the middle is unreadable) and a
 * worked example (a card sliced in half is a rendering bug, not a layout).
 *
 * `[&>*]:mb-3` rather than `space-y-3`, because `space-y` sets a top margin on every child but the
 * first, and "the first" is a document-order idea that means nothing once the children are dealt
 * into columns — the top of the second column would keep a margin the top of the first does not.
 */
const PAGE_CLASS =
  'text-sm text-muted-foreground lg:columns-2 lg:gap-6 [&>*]:mb-3 [&>*:last-child]:mb-0 [&_li]:break-inside-avoid [&>div]:break-inside-avoid';

function PageBody({ page }: { page: InstructionPage }) {
  if (page.kind === 'gameplay') return <GameplayPage />;
  if (page.kind === 'powerups') return <PowerUpsPage />;
  if (page.kind === 'star') return <StarPage />;
  // A `minigame` page always carries its game — `instructionPages` is the only thing that builds
  // one — and the guard is here so the narrowing is the compiler's rather than an assertion.
  return page.game === undefined ? null : <MiniGamePage game={page.game} />;
}

/**
 * Gameplay: the page this modal used to be, minus the power-ups that now have their own.
 *
 * **THE BINDINGS ARE NOT WRITTEN FROM MEMORY.** `ARENA_KEY_BINDINGS` in `game/input/actions.ts`
 * is the one place they are declared — SPACE / W / Q, the movement cross of arrows plus A, S and
 * D with W spent on the eject, and SHIFT held for the sprint — which is exactly the kind of thing
 * a hand-written page gets wrong a release later. The touch gestures are
 * `ArenaScene.onPointerDown` (a mouse double-click anywhere; a touch double-tap that has to land
 * on your own blob) and the labels on `ActionButtons`.
 *
 * The Duel line says what a duel *is* and stops there: what the mini-games are is one page per
 * game, generated, and this line pointing at them by name would be the hand-written list again.
 */
function GameplayPage() {
  return (
    <div className={PAGE_CLASS}>
      <ul className="space-y-2">
        <li>
          <Term>Move</Term> — your blob follows the pointer, or your thumb on a phone.{' '}
          <Keys>Arrows</Keys> or <Keys>A</Keys> <Keys>S</Keys> <Keys>D</Keys> steer too.
        </li>
        <li>
          <Term>Eat</Term> — pellets grow you slowly, smaller players grow you fast.
        </li>
        <li>
          <Term>Grow</Term> — the bigger you get, the slower you move. Nobody stays on top for free.
        </li>
        <li>
          <Term>Split</Term> — <Keys>Space</Keys>, double-click, or double-tap your own blob. Every
          cell halves and the new halves lunge forward, so you can catch something that was
          outrunning you — at the price of being in pieces. Hold it for 2 → 4 → 8.
        </li>
        <li>
          <Term>Eject</Term> — <Keys>W</Keys> throws a lump of your own mass out in front of you.
          Anyone can eat it, you included after a moment. It costs a little more than it is worth,
          which is what makes it bait rather than free food.
        </li>
        <li>
          <Term>Sprint</Term> — hold <Keys>Shift</Keys> to move half again as fast while the bar in
          the corner lasts. It empties in about four seconds and takes four times that to come back,
          so it is for closing one chase or breaking one — never for getting anywhere.
        </li>
        <li>
          <Term>Duel</Term> — collide with someone your own size and you both freeze into a short
          mini-game. Winner takes a bite; nobody else can touch either of you until it is over. A
          duel gun starts one at any size, for a much bigger pot. The pages beside this one are the
          games themselves.
        </li>
        <li>
          <Term>Spikes</Term> — the green stars shatter a blob that runs into one into pieces. Small
          players slip past them; big ones use them to break somebody open.
        </li>
      </ul>

      <p className="text-xs">
        <Term>On a phone</Term> — the buttons by your thumb are the same actions, with Leave beside
        them. Holding one repeats it exactly as holding the key does — except Run, which is held
        rather than pressed and sprints for exactly as long as your thumb is on it.
      </p>
    </div>
  );
}

/**
 * Power-ups, off the registry rather than out of prose.
 *
 * `POWER_UPS` in `@chompdome/shared/powerups` is the same list the orbs are rolled from and
 * carries the glyph the HUD's own inventory strip draws, so a tenth ability appears on this page
 * the day it is registered and cannot be described here as something it is not. How many a
 * player may carry is `POWER_UP_INVENTORY_SIZE` for the same reason — this page said "one slot"
 * for a release after that stopped being true.
 */
function PowerUpsPage() {
  return (
    <div className={PAGE_CLASS}>
      <p>
        Drive over a gold orb to pick one up, then <Keys>Q</Keys> to fire it. You carry up to{' '}
        {POWER_UP_INVENTORY_SIZE} at once and point at one with <Keys>1</Keys>–
        <Keys>{POWER_UP_INVENTORY_SIZE}</Keys> or <Keys>E</Keys>. Some are a window that is burning
        down whether you spend it or not — switching away from one of those ends it — and the rest
        are a number of uses that will wait for you.
      </p>
      <ul className="space-y-1.5">
        {POWER_UPS.map((powerUp) => (
          <li key={powerUp.id}>
            <span aria-hidden="true">{powerUp.glyph}</span>{' '}
            <span className="font-semibold text-foreground">{powerUp.name}</span> —{' '}
            {powerUp.description}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One mini-game's page: its own words, its own artwork, and — if it plays for one — its own pot.
 *
 * Nothing in here names a game. The prose is `MiniGameHowToPlay` declared in the game's own file,
 * the icons are its own `MiniGameChoiceArt`, and whether there is a pot is `miniGamePotOf` — the
 * same registry lookup `DuelOverlay` and `duel-pot.ts` already go through, and never a branch on
 * an id. A game that declares no prose still gets a page: its name, its answers and its pot.
 */
function MiniGamePage({ game }: { game: MiniGame }) {
  const howToPlay = miniGameHowToPlay(game);
  // The answer the reader taps, held here the way `GameConnection` holds it in a room — so the
  // real card behaves exactly as it does in game rather than being drawn inert.
  const [choice, setChoice] = useState<string | null>(null);
  // Bumped by Reset, which restarts the duel from zero so a reader can watch it again.
  const [attempt, setAttempt] = useState(0);
  /*
   * THE EXAMPLE RUNS. IT IS NOT A STILL PICTURE, AND THAT REVERSES HOW IT SHIPPED.
   *
   * It was frozen at the pot's top rung on `DuelPotBar`'s `settled` argument — a still picture of
   * the most informative frame. What that actually put on screen was a bar sitting at 80% doing
   * nothing but pulsing its danger animation, which teaches the one thing about chicken that
   * matters least. **The whole game is the climb**: the percentage IS the clock, the question is
   * only *when* to grab, and a reader who has never seen one needs to watch it move and feel the
   * ten seconds. The player: "the inside bar should start at 0% and grow over 10 seconds like the
   * real gameplay would."
   *
   * So the page drives its own clock. `startedAt` is fixed and `elapsed` advances a point at a
   * time, which is exactly what the wire does — the pot is a whole percent on every frame — so the
   * card is fed the same shape of value a room feeds it. It **loops**, because a demo that ran
   * once and stopped would be a still picture again for everybody who looked away.
   *
   * A grab FREEZES it where it stands, which is the room's own behaviour rather than a nicety:
   * the server freezes the climb at the settling tick, so the number beside the payout is the
   * number the payout was measured at. Reset starts it over.
   */
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  const frozen = choice !== null;
  useEffect(() => {
    if (frozen) return;
    const id = setInterval(() => {
      setElapsed((ms) => (ms + DUEL_EXAMPLE_STEP_MS) % DUEL_EXAMPLE_WINDOW_MS);
    }, DUEL_EXAMPLE_STEP_MS);
    return () => clearInterval(id);
  }, [frozen]);

  const percent = Math.min(
    MINI_GAME_POT_MAX_PERCENT,
    Math.floor((elapsed / DUEL_EXAMPLE_WINDOW_MS) * MINI_GAME_POT_MAX_PERCENT),
  );
  const duel = instructionDuel(game, startedAt, choice, percent);
  const pot = readDuelPot(duel.pot);

  return (
    <div className={PAGE_CLASS}>
      <h3 className="text-base font-bold text-foreground">{game.name}</h3>
      {howToPlay === undefined ? null : <p>{howToPlay.summary}</p>}

      {howToPlay === undefined ? null : (
        <ul data-testid="minigame-rules" className="list-disc space-y-1.5 pl-5">
          {howToPlay.rules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      )}

      {/*
       * THE REAL CARD, WITH THE CLOCK STOPPED — which is `StarPage`'s example one page along and
       * the same argument it makes: the player asked to "see it in action as it would show in
       * game", and what a duel actually looks like is a card, not a row of icons beside a bar.
       *
       * `DuelCard` is `DuelOverlay` minus the arena around it — no `role="dialog"` to nest inside
       * this one, and no absolutely-positioned overlay to have nothing to position against. It
       * takes a `DuelView` and nothing else, which is the property that let `DuelPotBar` and
       * `StarChoiceCard` be mounted here before it.
       *
       * **THIS REPLACED TWO SEPARATE EXAMPLES RATHER THAN JOINING THEM.** The page used to draw
       * the answers as inert tiles and the pot as a bare bar, which is two pieces of the card a
       * reader will meet, shown apart, in an arrangement that exists nowhere in the game. The
       * card contains both — the buttons ARE the answers and the bar is drawn where it really
       * sits — so keeping the tiles would have been a second drawing of a thing already on the
       * page, which is exactly what this modal exists not to have.
       *
       * It is built for whatever game the page is about (`instructionDuel`), so a game with no
       * pot gets a card with no bar and a third mini-game needs no edit here.
       */}
      <div data-testid="minigame-duel-example" className="rounded-xl border border-border p-3">
        <p className="mb-2 text-xs">
          The card you get in a duel, held still. Try it — nothing here is connected to a room, and
          the countdown is stopped.
        </p>
        <DuelCard key={attempt} duel={duel} onChoose={setChoice} now={() => startedAt + elapsed} />
        {pot === null ? null : (
          <p className="mt-2 text-xs">
            Grabbing at {pot.percent}% is worth {pot.multiplier.toFixed(2)}x the duel's stake;
            grabbing under {pot.penaltyBand === null ? 0 : pot.penaltyBand.toPercent}% may cost you{' '}
            {pot.earlyGrabPenalty.toFixed(2)}x of it instead. Where this one bursts and where it
            arms are drawn per duel and nobody is told — not even your own screen.
          </p>
        )}
        <button
          type="button"
          data-testid="minigame-example-reset"
          onClick={() => {
            setChoice(null);
            setElapsed(0);
            setAttempt((n) => n + 1);
          }}
          className="mt-2 cursor-pointer rounded-md bg-secondary/60 px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          Reset the example
        </button>
      </div>
    </div>
  );
}

function StarPage() {
  // A frozen instant, taken once: the example is a still picture and its ten seconds are the
  // full ten. `DuelPotBar`'s `settled` on the page beside this one, for its reason.
  const [sampleNow] = useState(() => Date.now());
  // The answer the reader taps, held here the way `GameConnection` holds it in a room — so the
  // real card can behave exactly as it does in game rather than being drawn disabled.
  const [choice, setChoice] = useState<string | null>(null);
  // Bumped by Reset, which re-mounts the card. It is what recovers from "Take the share" and
  // from the panel's ✕, both of which close the card in game and do so here too.
  const [attempt, setAttempt] = useState(0);

  return (
    <div className={PAGE_CLASS}>
      <h3 className="text-base font-bold text-foreground">The star</h3>
      <p>
        Every few minutes a star appears somewhere on the map — the biggest thing on the field,
        rainbow-filled and labelled <Term>{STAR_FIELD_LABEL}</Term>. There is never more than{' '}
        {MAX_STARS_PER_ROOM} at a time, so the one you can see is always the one that works.
      </p>

      <ul className="space-y-2">
        <li>
          <Term>Only a human can take it</Term> — a bot will drive straight over a star and leave it
          exactly where it is. If you watch one do that, nothing is broken: it is yours.
        </li>
        <li>
          <Term>Taking it asks the whole room</Term> — everybody gets the same question and{' '}
          {VOTE_DURATION_SECONDS} seconds to answer it. <Term>Nothing freezes.</Term> Every player
          in the room, you included, keeps steering, eating and duelling for all{' '}
          {VOTE_DURATION_SECONDS} seconds.
        </li>
        <li>
          <Term>The majority wins</Term> — whichever answer got the most votes. The minority pays{' '}
          {Math.round(VOTE_LOSS_SHARE * 100)}% of what they were carrying at the moment they
          answered, and the winners split that between them, one share each.
        </li>
        <li>
          <Term>A vote can never kill you</Term> — what it takes is floored, so being on the losing
          side is expensive and survivable. Saying nothing at all costs nothing and pays nothing:
          answering is a choice, not a deadline you have to beat.
        </li>
        <li>
          <Term>A tie decides nothing</Term> — no mass moves, nobody is paid, and nobody dies.
        </li>
      </ul>

      <p>
        <Term>And then the part that is only yours.</Term> Whoever took the star gets a different
        card from everybody else, with two ways out: take a guaranteed winner's share of the pot, or
        call it yourself for {STAR_GAMBLE_PAYOUT_MULTIPLIER}x your own mass — capped at whatever the
        pot actually holds — and <Term>die if you are wrong</Term>. Say nothing, close the card, or
        run out of time and you take the share; the call is the only thing that can end your run.
      </p>

      {/*
       * THE REAL CARD, WITH THE CLOCK STOPPED. `StarChoiceCard` takes a `VoteView` and a mass and
       * nothing else — no connection, no wire — which is exactly what let `DuelPotBar` be mounted
       * on the page beside this one. The prompt and the two answers on it are the room's own
       * (`STAR_VOTE_PROMPT`, `STAR_VOTE_OPTIONS`), and the payout figure is `starGamblePayout` of
       * the sample mass, so what a reader sees here is the arithmetic the room will actually do.
       */}
      <div data-testid="star-card-example" className="rounded-xl border border-border p-3">
        <p className="mb-2 text-xs">
          The card you get, held still, drawn for a player carrying{' '}
          {INSTRUCTION_STAR_SAMPLE_MASS.toLocaleString()} mass. Try it — nothing here is connected
          to a room.
        </p>
        <StarChoiceCard
          key={attempt}
          vote={instructionStarVote(sampleNow, choice)}
          mass={INSTRUCTION_STAR_SAMPLE_MASS}
          onAnswer={setChoice}
          now={() => sampleNow}
        />
        <button
          type="button"
          data-testid="star-example-reset"
          onClick={() => {
            setChoice(null);
            setAttempt((n) => n + 1);
          }}
          className="mt-2 cursor-pointer rounded-md bg-secondary/60 px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
        >
          Reset the example
        </button>
      </div>
    </div>
  );
}

function Term({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-foreground">{children}</span>;
}

/** One key cap. The same treatment the arena's own control hint uses, so the two teach the same thing. */
function Keys({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-border/60 bg-secondary/60 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-foreground">
      {children}
    </kbd>
  );
}
